/**
 * TasksModule — Cabinet › Tâches & check-listes (Batch #14.285).
 * Module unifié : une tâche = fiche par dossier Master (expertise, type, priorité, échéance) + étapes à cocher (ex-check-listes).
 * Vues : Liste (tableau) · Tableau (Kanban glisser-déposer) · Terminées. Panneau latéral pour créer / modifier.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Briefcase, ListChecks, Loader2, Plus } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Btn, errMsg } from "./comptabilite/_shared";
import { TASK_STATUSES } from "./tasks/taskHelpers";
import TaskToolbar from "./tasks/TaskToolbar";
import TaskTable from "./tasks/TaskTable";
import TaskBoard from "./tasks/TaskBoard";
import TaskDrawer from "./tasks/TaskDrawer";

const VIEWS = [{ id: "list", label: "Liste" }, { id: "board", label: "Tableau" }, { id: "done", label: "Terminées" }];
const NO_FILTER = { q: "", master_id: "", expertise: "", task_type: "", priority: "", status: "", due: "", tax_year: "" };

function Kpi({ label, value, tone = "slate", active, onClick, testId }) {
  const tones = { slate: "bg-white border-slate-200 text-slate-900", rose: "bg-rose-50 border-rose-200 text-rose-900", amber: "bg-amber-50 border-amber-200 text-amber-900", sky: "bg-sky-50 border-sky-200 text-sky-900", emerald: "bg-emerald-50 border-emerald-200 text-emerald-900" };
  return (
    <button onClick={onClick} className={`border rounded-xl p-3 text-left transition hover:shadow-sm ${tones[tone]} ${active ? "ring-2 ring-emerald-400" : ""}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{label}</div>
      <div className="text-lg font-bold tabular-nums mt-0.5">{value ?? "—"}</div>
    </button>
  );
}

export default function TasksModule() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const view = VIEWS.some((v) => v.id === sp.get("view")) ? sp.get("view") : "list";
  const [f, setF] = useState({ ...NO_FILTER, due: sp.get("due") || "" });
  const [masters, setMasters] = useState([]);
  const [alertSettings, setAlertSettings] = useState(null);
  const [tasks, setTasks] = useState(null);
  const [stats, setStats] = useState(null);
  const [drawer, setDrawer] = useState(null); // null | "new" | task

  useEffect(() => {
    api.get("/masters").then((r) => setMasters(r.data?.items || [])).catch(() => setMasters([]));
    api.get("/tasks/settings").then((r) => setAlertSettings(r.data)).catch(() => setAlertSettings({ default_alerts: [] }));
  }, []);

  useEffect(() => {
    if (sp.get("action") === "new") { setDrawer("new"); const n = new URLSearchParams(sp); n.delete("action"); setSp(n, { replace: true }); }
  }, [sp, setSp]);

  const loadStats = useCallback(() => api.get("/tasks/stats").then((r) => setStats(r.data)).catch(() => {}), []);

  const load = useCallback(async () => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    params.view = view === "done" ? "done" : view === "board" ? "all" : "open";
    if (view === "done") delete params.status;
    try { const r = await api.get("/tasks", { params }); setTasks(r.data.items || []); }
    catch (e) { toast.error(errMsg(e, "Chargement des tâches impossible")); setTasks([]); }
  }, [f, view]);

  useEffect(() => { load(); loadStats(); }, [load, loadStats]);

  const setView = (id) => { const n = new URLSearchParams(sp); if (id === "list") n.delete("view"); else n.set("view", id); setSp(n, { replace: true }); };
  const refresh = () => { load(); loadStats(); };

  const setStatus = async (t, status) => {
    setTasks((p) => p.map((x) => (x.id === t.id ? { ...x, status } : x)));
    try {
      const r = await api.post(`/tasks/${t.id}/status`, { status });
      if (r.data.rolled) toast.success(`Occurrence terminée — prochaine échéance le ${r.data.due_date}`);
      refresh();
    }
    catch (e) { toast.error(errMsg(e)); refresh(); }
  };
  const duplicate = async (t) => {
    try { const r = await api.post(`/tasks/${t.id}/duplicate`); toast.success("Copie créée"); refresh(); setDrawer(r.data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Supprimer « ${t.title} » ?`)) return;
    try { await api.delete(`/tasks/${t.id}`); toast.success("Tâche supprimée"); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const onDrawerChanged = (t, close) => { refresh(); if (close) setDrawer(null); else if (t) setDrawer(t); };

  const kpiFilter = (patch) => { setF({ ...NO_FILTER, ...patch }); if (view === "done") setView("list"); };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-4" data-testid="tasks-module">
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={() => navigate("/admin/cabinet")} className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center transition active:scale-90" aria-label="Retour" data-testid="tasks-back-btn">
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div className="flex items-center gap-2 text-sm text-slate-500 flex-1 min-w-0">
          <Briefcase className="w-4 h-4 text-emerald-600 hidden sm:inline" />
          <span className="font-medium hidden sm:inline">Cabinet</span>
          <span className="text-slate-300 hidden sm:inline">›</span>
          <span className="font-bold text-slate-800 inline-flex items-center gap-1.5 truncate"><ListChecks className="w-4 h-4 shrink-0" /> Tâches & check-listes</span>
        </div>
        {stats && (
          <div className="hidden sm:flex gap-3 text-xs text-slate-500" data-testid="tasks-kpis-inline">
            <span><strong className="text-slate-800" data-testid="tasks-kpi-open">{stats.total_open}</strong> ouverte{stats.total_open > 1 ? "s" : ""}</span>
            {stats.overdue > 0 && <span className="text-rose-700"><strong data-testid="tasks-kpi-overdue-inline">{stats.overdue}</strong> en retard</span>}
          </div>
        )}
        <Btn icon={Plus} onClick={() => setDrawer("new")} testId="task-new-btn">Nouvelle tâche</Btn>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="task-stats">
        <Kpi label="En retard" value={stats?.overdue} tone={stats?.overdue ? "rose" : "slate"} active={f.due === "overdue"} onClick={() => kpiFilter({ due: "overdue" })} testId="kpi-overdue" />
        <Kpi label="Aujourd'hui" value={stats?.today} tone="amber" active={f.due === "today"} onClick={() => kpiFilter({ due: "today" })} testId="kpi-today" />
        <Kpi label="7 prochains jours" value={stats?.week} tone="sky" active={f.due === "week"} onClick={() => kpiFilter({ due: "week" })} testId="kpi-week" />
        {TASK_STATUSES.filter((s) => s.id !== "terminee").map((s) => (
          <Kpi key={s.id} label={s.label} value={stats?.by_status?.[s.id]} active={f.status === s.id} onClick={() => kpiFilter({ status: s.id })} testId={`kpi-${s.id}`} />
        ))}
        <Kpi label="Terminées · 30 j" value={stats?.done_30d} tone="emerald" active={view === "done"} onClick={() => { setF(NO_FILTER); setView("done"); }} testId="kpi-done" />
      </div>

      <div className="flex gap-1 border-b overflow-x-auto">
        {VIEWS.map((v) => (
          <button key={v.id} onClick={() => setView(v.id)} className={`px-4 py-2 text-sm border-b-2 transition whitespace-nowrap ${view === v.id ? "border-emerald-600 text-emerald-700 font-semibold" : "border-transparent text-gray-500 hover:text-gray-700"}`} data-testid={`tasks-view-${v.id}`}>
            {v.label}
          </button>
        ))}
      </div>

      <TaskToolbar f={f} setF={setF} masters={masters} showStatus={view === "list"} count={tasks?.length || 0} />

      {tasks === null ? (
        <div className="p-10 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : view === "board" ? (
        <TaskBoard tasks={tasks} onOpen={setDrawer} onStatus={setStatus} />
      ) : (
        <TaskTable tasks={tasks} done={view === "done"} onOpen={setDrawer} onStatus={setStatus} onDuplicate={duplicate} onDelete={remove} onNew={() => setDrawer("new")} />
      )}

      <p className="text-[11px] text-slate-400">
        Une check-liste est une tâche avec des étapes à cocher : ajoutez les étapes dans le panneau, puis « Dupliquer » pour réutiliser la même liste sur un autre dossier.
        Glissez les cartes entre les colonnes du Tableau pour changer de statut.
      </p>

      {drawer && <TaskDrawer task={drawer === "new" ? null : drawer} masters={masters} defaultAlerts={alertSettings?.default_alerts || []} onClose={() => setDrawer(null)} onChanged={onDrawerChanged} />}
    </div>
  );
}
